import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, InputNumber, Message, Schema, SelectPicker, DatePicker } from 'rsuite';
import { useAuth } from '../../contexts/AuthContext';
import { canEditField, canViewField } from '../../utils/permissionUtils';
import './EmployeeForm.css';
import { useCoachOptions, useEntraUserByEmail } from '../../hooks/useQueries';
import SharePointLocationPicker from './SharePointLocationPicker';
import PermissionPicker from './PermissionPicker';
import { toGrantedPermissionList, toPermissionGrantsObject } from '../../config/permissions';

const { StringType, NumberType, DateType } = Schema.Types;

const Textarea = React.forwardRef(function Textarea(
  { value, onChange, className, ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value, e)}
      className={['rs-input', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

const formatCurrencyInput = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const startOfDay = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const parseMonthDay = (value) => {
  if (!value) return { month: null, day: null };
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { month: null, day: null };
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(month) || !Number.isFinite(day)) return { month: null, day: null };
  return { month, day };
};

const toBirthdayDateString = (month, day) => {
  if (!month || !day) return null;
  const daysInMonth = new Date(2000, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `2000-${mm}-${dd}`;
};

const MONTH_OPTIONS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

/*
  `hideSubmit` takes the button out and leaves submitting to whoever holds the
  ref — a modal that wants its action in the footer, where the rest of its
  buttons are. The ref exposes `submit()`, which runs the same validation the
  inline button does.
*/
const EmployeeForm = forwardRef(function EmployeeForm({
  initialValue,
  roles,
  onSubmit,
  submitLabel = 'Save',
  loading = false,
  error,
  hideSubmit = false,
}, ref) {
  const auth = useAuth();
  const user = { role: auth.role, employee: auth.employee };
  const formRef = useRef();
  const formTopRef = useRef(null);
  const isCreateMode = !initialValue?.id;

  const coachOptionsQuery = useCoachOptions({ enabled: auth.isAdmin() });
  const entraEmailLookup = useEntraUserByEmail();

  const preferencesToText = (pref) => {
    if (pref == null) return '';
    if (typeof pref === 'string') return pref;
    try {
      return JSON.stringify(pref, null, 2);
    } catch {
      return '';
    }
  };

  const [preferencesError, setPreferencesError] = useState(null);
  const [entraLookupError, setEntraLookupError] = useState(null);
  /*
    A new record starts as one field: the email to look the person up by. The
    rest of the form arrives once there is somebody to fill it with, so nobody
    is faced with twenty inputs before the one that answers most of them.

    Editing an existing employee shows everything, as does a form for somebody
    who cannot look anyone up — there is nothing to wait for in either case.
  */
  const [foundInEntra, setFoundInEntra] = useState(false);
  const [enteringManually, setEnteringManually] = useState(false);

  // The address the lookup answered for. Held so the button can say the account
  // was found and stop offering to find it again — until the field is edited,
  // at which point it is a different address and the answer no longer applies.
  const [foundForEmail, setFoundForEmail] = useState(null);

  const [formValue, setFormValue] = useState(() => ({
    azure_oid: initialValue?.azure_oid || '',
    email: initialValue?.email || '',
    first_name: initialValue?.first_name || '',
    last_name: initialValue?.last_name || '',
    phone: initialValue?.phone || '',
    address: initialValue?.address || '',
    birthday_month: parseMonthDay(initialValue?.birthday).month,
    birthday_day: parseMonthDay(initialValue?.birthday).day,
    job_title: initialValue?.job_title || '',
    hire_date: initialValue?.hire_date ? new Date(initialValue.hire_date) : null,
    coach_id: initialValue?.coach_id || null,
    preferences: preferencesToText(initialValue?.preferences),
    role_id: initialValue?.role_id || '',
    granted_permissions: toGrantedPermissionList(initialValue?.permission_grants),
    timecamp_user_id: initialValue?.timecamp_user_id || '',
    pay_rate: initialValue?.pay_rate ?? null,
    document_library_id: initialValue?.sharepoint_drive_id || '',
    effective_date: null,
    notes: '',
    is_active: typeof initialValue?.is_active === 'boolean' ? initialValue.is_active : true,
  }));

  // True only while the field still holds the address that was found.
  const emailAlreadyFound = Boolean(foundForEmail)
    && String(formValue.email || '').trim().toLowerCase() === String(foundForEmail).trim().toLowerCase();

  const needsLookup = isCreateMode && auth.isAdmin();
  const showDetails = !needsLookup
    || foundInEntra
    || enteringManually
    || Boolean(String(initialValue?.azure_oid || '').trim());

  const model = useMemo(() => {
    const base = {
      email: StringType().isEmail('Invalid email'),
      first_name: StringType().isRequired('First name is required'),
      last_name: StringType().isRequired('Last name is required'),
      job_title: StringType(),
      hire_date: DateType(),
      pay_rate: NumberType(),
    };

    if (auth.isAdmin()) {
      base.azure_oid = StringType().isRequired('Azure OID is required');
      base.email = base.email.isRequired('Email is required');
    }

    return Schema.Model(base);
  }, [auth]);

  const roleOptions = useMemo(() => {
    return (roles || []).map((r) => ({ label: r.name, value: r.id }));
  }, [roles]);

  const coachOptions = useMemo(() => {
    const options = coachOptionsQuery.data || [];
    const selfId = initialValue?.id;
    if (!selfId) return options;
    return options.filter((o) => o?.value !== selfId);
  }, [coachOptionsQuery.data, initialValue?.id]);

  useImperativeHandle(ref, () => ({ submit: () => handleSubmit() }));

  const handleSubmit = async () => {
    /*
      A submit while the form is still one field — the modal's own button sits
      in its footer and does not know that — would fail validation against
      inputs nobody can see. Say what is missing instead; the manual-entry way
      through appears alongside it.
    */
    if (!showDetails) {
      setEntraLookupError('Find the user by email first, or enter the details manually.');
      return;
    }

    if (!formRef.current?.check()) return;

    setPreferencesError(null);

    let parsedPreferences = null;
    if (!isCreateMode) {
      const rawPreferences = String(formValue.preferences ?? '').trim();
      if (rawPreferences.length > 0) {
        try {
          parsedPreferences = JSON.parse(rawPreferences);
        } catch {
          setPreferencesError('Preferences must be valid JSON');
          return;
        }
      }
    }

    if ((formValue.birthday_month && !formValue.birthday_day) || (!formValue.birthday_month && formValue.birthday_day)) {
      setPreferencesError('Birthday requires both month and day');
      return;
    }

    const parsedBirthday =
      formValue.birthday_month && formValue.birthday_day
        ? toBirthdayDateString(Number(formValue.birthday_month), Number(formValue.birthday_day))
        : null;

    if ((formValue.birthday_month && formValue.birthday_day) && !parsedBirthday) {
      setPreferencesError('Birthday month/day is invalid');
      return;
    }

    const payload = {
      ...formValue,
      birthday: parsedBirthday,
      hire_date: formValue.hire_date ? formValue.hire_date.toISOString().slice(0, 10) : null,
      effective_date: formValue.effective_date ? formValue.effective_date.toISOString().slice(0, 10) : null,
      preferences: isCreateMode ? null : parsedPreferences,
      permission_grants: toPermissionGrantsObject(formValue.granted_permissions),
      sharepoint_drive_id: formValue.document_library_id || null,
      document_library_id: formValue.document_library_id || null,
      sharepoint_folder_path: null,
    };
    await onSubmit?.(payload);
  };

  /*
    Find the account by email and fill the form from it, the OID included.

    Nobody has an Azure object id to hand — they have the person's address — so
    that is what the field asks for. The id is still shown below and still
    editable, because it is what the record is keyed on and a lookup that finds
    nobody has to leave a way through.
  */
  const handleFetchByEmail = async () => {
    const email = String(formValue.email || '').trim();
    if (!email) {
      setEntraLookupError('Enter an email address first.');
      return;
    }

    setEntraLookupError(null);

    try {
      const user = await entraEmailLookup.mutateAsync(email);
      setFoundInEntra(true);
      // Entra may answer on the sign-in name when the mailbox was asked for, so
      // this records the address that ends up in the field, not the typed one.
      setFoundForEmail(user?.email || email);
      setFormValue((prev) => ({
        ...prev,
        azure_oid: user?.azure_oid || prev.azure_oid,
        email: user?.email || prev.email,
        first_name: user?.first_name || prev.first_name,
        last_name: user?.last_name || prev.last_name,
        phone: user?.phone || prev.phone,
        address: user?.address || prev.address,
        job_title: user?.job_title || prev.job_title,
      }));
    } catch (err) {
      setEntraLookupError(String(err?.message || err));
    }
  };

  useEffect(() => {
    if (!error && !preferencesError && !entraLookupError) return;
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [error, preferencesError, entraLookupError]);

  return (
    <div className="employee-form">
      <div ref={formTopRef} />
      {error ? (
        <Message type="error" showIcon header="Error" style={{ marginBottom: 12 }}>
          {String(error?.message || error)}
        </Message>
      ) : null}

      {preferencesError ? (
        <Message type="error" showIcon header="Validation" style={{ marginBottom: 12 }}>
          {preferencesError}
        </Message>
      ) : null}

      {entraLookupError ? (
        <Message type="error" showIcon header="Entra Lookup" style={{ marginBottom: 12 }}>
          {entraLookupError}
        </Message>
      ) : null}

      <Form
        ref={formRef}
        fluid
        model={model}
        formValue={formValue}
        onChange={setFormValue}
      >
        {auth.isAdmin() ? (
          <>
            <Form.Group controlId="email">
              <Form.ControlLabel>Email</Form.ControlLabel>
              <div className="d-flex gap-2 flex-wrap align-items-end">
                <Form.Control name="email" accepter={Input} style={{ flex: 1 }} />
                {/* Nothing to look up until something has been typed, and
                    nothing left to look up once this address has answered. */}
                {emailAlreadyFound ? (
                  <Button appearance="ghost" color="green" disabled>
                    <i className="bi bi-check-lg me-1" />
                    User found
                  </Button>
                ) : String(formValue.email || '').trim() ? (
                  <Button
                    appearance="ghost"
                    onClick={handleFetchByEmail}
                    loading={entraEmailLookup.isPending}
                  >
                    Find user by email
                  </Button>
                ) : null}
              </div>
            </Form.Group>

          </>
        ) : null}

        {/* A lookup that found nobody cannot be the end of the road: the person
            may simply not be in Entra yet. */}
        {needsLookup && !showDetails && entraLookupError ? (
          <div className="employee-form__manual">
            <Button appearance="link" onClick={() => setEnteringManually(true)}>
              Enter the details manually
            </Button>
          </div>
        ) : null}

        <div className="employee-form__details" data-open={showDetails ? 'true' : 'false'}>
          <div className="employee-form__detailsinner" inert={showDetails ? undefined : ''}>
          {auth.isAdmin() ? (
            /* Filled by the lookup, and left editable: it is what the record is
               keyed on, and somebody who has the id can still type it. */
            <Form.Group controlId="azure_oid">
              <Form.ControlLabel>Azure OID</Form.ControlLabel>
              <Form.Control name="azure_oid" accepter={Input} />
            </Form.Group>
          ) : null}

          <div className="row employee-form__row">
            <div className="col-12 col-md-6">
              <Form.Group controlId="first_name">
                <Form.ControlLabel>First Name</Form.ControlLabel>
                <Form.Control name="first_name" accepter={Input} disabled={!canEditField(user, 'first_name') && !auth.isAdmin()} />
              </Form.Group>
            </div>
            <div className="col-12 col-md-6">
              <Form.Group controlId="last_name">
                <Form.ControlLabel>Last Name</Form.ControlLabel>
                <Form.Control name="last_name" accepter={Input} disabled={!canEditField(user, 'last_name') && !auth.isAdmin()} />
              </Form.Group>
            </div>
          </div>

          <div className="row employee-form__row">
            <div className="col-12 col-md-6">
              <Form.Group controlId="phone">
                <Form.ControlLabel>Phone</Form.ControlLabel>
                <Form.Control name="phone" accepter={Input} disabled={!canEditField(user, 'phone') && !auth.isAdmin()} />
              </Form.Group>
            </div>
            <div className="col-12 col-md-6">
              <Form.Group controlId="address">
                <Form.ControlLabel>Address</Form.ControlLabel>
                <Form.Control name="address" accepter={Input} disabled={!canEditField(user, 'address') && !auth.isAdmin()} />
              </Form.Group>
            </div>
          </div>

          <Form.Group controlId="job_title">
            <Form.ControlLabel>Job Title</Form.ControlLabel>
            <Form.Control name="job_title" accepter={Input} disabled={!canEditField(user, 'job_title') && !auth.isAdmin()} />
          </Form.Group>

          <div className="row employee-form__row">
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group controlId="coach_id">
                <Form.ControlLabel>Coach</Form.ControlLabel>
                {auth.isAdmin() ? (
                  <Form.Control
                    name="coach_id"
                    accepter={SelectPicker}
                    data={coachOptions}
                    searchable
                    cleanable
                    placeholder="Select coach"
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Input
                    value={initialValue?.coach_name || ''}
                    disabled
                    placeholder={initialValue?.coach_name ? undefined : 'Not assigned'}
                  />
                )}
              </Form.Group>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Group controlId="hire_date">
                <Form.ControlLabel>Hire Date</Form.ControlLabel>
                <Form.Control name="hire_date" accepter={DatePicker} disabled={!auth.isAdmin()} oneTap isoWeek={false} weekStart={0} format="MM/dd/yyyy" style={{ width: '100%' }} />
              </Form.Group>
            </div>
            {auth.isAdmin() ? (
              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group controlId="timecamp_user_id">
                  <Form.ControlLabel>TimeCamp User ID (optional)</Form.ControlLabel>
                  <Form.Control name="timecamp_user_id" accepter={Input} placeholder="e.g. 123456" />
                </Form.Group>
              </div>
            ) : null}

            {!isCreateMode && auth.isAdmin() ? (
              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group controlId="role_id">
                  <Form.ControlLabel>Role</Form.ControlLabel>
                  <Form.Control
                    name="role_id"
                    accepter={SelectPicker}
                    data={roleOptions}
                    searchable={false}
                    cleanable={false}
                    placeholder="Select role"
                    style={{ width: '100%' }}
                  />
                </Form.Group>
              </div>
            ) : null}
          </div>

          {auth.isAdmin() ? (
            <Form.Group controlId="granted_permissions">
              <Form.ControlLabel>Permission Grants</Form.ControlLabel>
              <Form.Control
                name="granted_permissions"
                accepter={PermissionPicker}
                placeholder="Select granted permissions"
                /* Near the foot of a tall form, so it opens over the fields
                   above rather than off the bottom of the dialog. */
                placement="topStart"
                preventOverflow
              />
            </Form.Group>
          ) : null}

          {auth.isAdmin() ? (
            <>
              <div className="employee-form__sectiontitle">Document Library</div>

              <SharePointLocationPicker
                value={{
                  sharepoint_drive_id: formValue.document_library_id,
                  document_library_id: formValue.document_library_id,
                }}
                onChange={(nextValue) =>
                  setFormValue((prev) => ({
                    ...prev,
                    document_library_id: nextValue.sharepoint_drive_id || nextValue.document_library_id || null,
                  }))
                }
                disabled={loading}
                saving={loading}
              />
            </>
          ) : null}

          {!isCreateMode ? (
            <Form.Group controlId="preferences">
              <Form.ControlLabel>Preferences (JSON)</Form.ControlLabel>
              <Form.Control
                name="preferences"
                accepter={Textarea}
                rows={4}
                disabled={!canEditField(user, 'preferences') && !auth.isAdmin()}
                placeholder='e.g. { "theme": "dark" }'
              />
            </Form.Group>
          ) : null}

          <div className="row employee-form__row">
            <div className="col-12 col-md-6">
              <Form.Group controlId="birthday_month">
                <Form.ControlLabel>Birthday Month</Form.ControlLabel>
                <Form.Control
                  name="birthday_month"
                  accepter={SelectPicker}
                  data={MONTH_OPTIONS}
                  searchable
                  cleanable
                  placeholder="Month"
                  disabled={!auth.isAdmin()}
                  style={{ width: '100%' }}
                  placement="topStart"
                  preventOverflow
                />
              </Form.Group>
            </div>
            <div className="col-12 col-md-6">
              <Form.Group controlId="birthday_day">
                <Form.ControlLabel>Birthday Day</Form.ControlLabel>
                <Form.Control
                  name="birthday_day"
                  accepter={SelectPicker}
                  data={Array.from({ length: new Date(2000, Number(formValue.birthday_month || 1), 0).getDate() }, (_v, idx) => ({
                    label: String(idx + 1),
                    value: idx + 1,
                  }))}
                  searchable
                  cleanable
                  placeholder="Day"
                  disabled={!auth.isAdmin() || !formValue.birthday_month}
                  style={{ width: '100%' }}
                  placement="topStart"
                  preventOverflow
                />
              </Form.Group>
            </div>
          </div>

          {auth.isAdmin() ? (
            <>
              <div className="row employee-form__row">
                {canViewField(user, 'pay_rate') ? (
                  <div className="col-12 col-md-6">
                    <Form.Group controlId="pay_rate">
                      <Form.ControlLabel>Pay Rate</Form.ControlLabel>
                      <Form.Control
                        name="pay_rate"
                        accepter={InputNumber}
                        min={0}
                        step={0.25}
                        prefix="$"
                        formatter={formatCurrencyInput}
                        style={{ width: '100%' }}
                      />
                    </Form.Group>
                  </div>
                ) : null}

                <div className="col-12 col-md-6">
                  <Form.Group controlId="effective_date">
                    <Form.ControlLabel>Pay Rate Effective Date</Form.ControlLabel>
                    <Form.Control
                      name="effective_date"
                      accepter={DatePicker}
                      oneTap
                      isoWeek={false}
                      weekStart={0}
                      format="MM/dd/yyyy"
                      placement="topStart"
                      preventOverflow
                      shouldDisableDate={(date) => {
                        const hire = startOfDay(formValue.hire_date);
                        const candidate = startOfDay(date);
                        if (!hire || !candidate) return false;
                        return candidate < hire;
                      }}
                      style={{ width: '100%' }}
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group controlId="notes">
                <Form.ControlLabel>Pay Rate Notes</Form.ControlLabel>
                <Form.Control name="notes" accepter={Input} />
              </Form.Group>
            </>
          ) : null}
          </div>
        </div>

        {hideSubmit || !showDetails ? null : (
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button appearance="primary" loading={loading} onClick={handleSubmit}>
              {submitLabel}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
});

export default EmployeeForm;
